import { useCallback } from 'react';
import { useNoticeBannersStackContext } from '@sg/garnish';

import { useCustomer } from '@order/Customer';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';
import { useTelemetry } from '@order/Telemetry';

import { useCancelOrderMutation } from '../../graphql';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns helpers to handle in-app order cancellations.
 */
export const useCancelOrder = (params: UseOrderCancelParams) => {
  const { orderId, restaurantId, onSuccess, onError } = params;

  // ─────────────────────────────────────────────────────────────────────

  const { track } = useTelemetry();

  const isInAppOrderCancellationEnabled = useFeatureFlag(
    'CELS-1476-in-app-order-cancellation',
  );
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { customer } = useCustomer();
  const { t } = useLocalizationContext();

  const customerId = customer.id ?? '';

  // ─── Data ────────────────────────────────────────────────────────────

  const [cancelPendingOrderResponse, cancelPendingOrder] =
    useCancelOrderMutation();

  const { fetching: isCancellingOrder } = cancelPendingOrderResponse;

  // ─── Helpers ─────────────────────────────────────────────────────────

  /**
   * Handles successful order cancellation event by displaying the associated
   * notice banner, tracking the event, and triggering the optional
   * `onSuccess` callback.
   */
  const handleSuccess = useCallback(() => {
    addNoticeBanner({
      text: t('order-cancellation.success'),
      palette: 'success',
    });
    track('order_cancel.success');

    onSuccess?.();
  }, [addNoticeBanner, onSuccess, t, track]);

  /**
   * Handles failed order cancellation event by displaying the associated
   * notice banner, tracking the event, and triggering the optional
   * `onError` callback.
   */
  const handleError = useCallback(
    (errorParams: UseOrderCancelHandleErrorParams) => {
      const { id, text, reason } = errorParams;

      addNoticeBanner({ id, text, palette: 'caution' }, true);
      track('order_cancel.failure', { reason });

      onError?.();
    },
    [addNoticeBanner, onError, track],
  );

  /**
   * Attempts to cancel the order using the associated mutation.
   * - Handles both successful and unsuccessful responses.
   * - Returns the resolved data.
   */
  const cancelOrder = useCallback(async () => {
    const hasRequiredParams = Boolean(customerId && orderId && restaurantId);

    if (!isInAppOrderCancellationEnabled || !hasRequiredParams) return;

    const response = await cancelPendingOrder(
      {
        input: {
          orderId,
          customerId,
          restaurantId,
          reason: 'customer initiated cancel',
          origin: 'customer cancel',
          refund: true,
        },
      },
      { requestPolicy: 'cache-and-network' },
    );

    const responseData = response.data?.cancelOrder;
    const responseTypename = responseData?.__typename ?? '';

    // ─── Success ─────────────────────────────────────────────────────────

    const hasSuccessfullyCanceledOrder =
      responseData?.__typename === 'CancelOrderSuccess' && responseData.success;

    if (hasSuccessfullyCanceledOrder) {
      handleSuccess();

      return responseData;
    }

    /*
      This is a partially successful case in which we need to display an
      error banner but also call the `onSuccess` callback because the
      order was canceled but the refund failed.
     */
    if (responseData?.__typename === 'CancelSuccessRefundFailed') {
      handleError({
        id: responseTypename,
        text: t('order-cancellation.error.refund-failed'),
        reason: responseData.reason,
      });
      onSuccess?.();

      return;
    }

    // ─── Errors ──────────────────────────────────────────────────────────

    const isSpecialCaseError =
      responseData?.__typename === 'CancellationFailedWithDelivery' ||
      responseData?.__typename === 'CancellationLimitExceeded' ||
      responseData?.__typename === 'OrderAlreadyCanceled';
    const isGenericError = responseData?.__typename === 'CancelOrderFailed';

    if (isSpecialCaseError) {
      handleError({
        id: responseTypename,
        text: t('order-cancellation.error.special'),
        reason: responseData.errorMessage,
      });

      return;
    }

    const errorMessage = isGenericError
      ? responseData.errorMessage
      : t('order-cancellation.error.generic');

    handleError({
      id: responseTypename,
      text: t('order-cancellation.error.generic'),
      reason: errorMessage,
    });
  }, [
    customerId,
    handleError,
    handleSuccess,
    isInAppOrderCancellationEnabled,
    orderId,
    restaurantId,
    t,
    cancelPendingOrder,
    onSuccess,
  ]);

  // ─────────────────────────────────────────────────────────────────────

  return { cancelOrder, isCancellingOrder };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseOrderCancelParams = Readonly<{
  orderId: string;
  restaurantId: string;
  onSuccess?: () => void;
  onError?: () => void;
}>;

type UseOrderCancelHandleErrorParams = Readonly<{
  id: string;
  text: string;
  reason: string;
}>;
